import React, { FocusEvent } from 'react'
import { useFormContext } from 'react-hook-form'
import Decimal from 'decimal.js'
import {
	MoneyField,
	PercentField,
	Select,
	TextField,
	ZipCodeField,
} from '../../../../components/formControls'
import { applyMask } from '../../../../services/utils'
import { getCityCountyStateFromZip } from '../utils/helpers'
import useUser from '../../../../hooks/useUser'
import { LoanPurposeTypes } from '../forms/LoanCalculatorForm'
import { currencyToFloat } from '../../../../services/formattingUtils'
import {
	SHARED_LOAN_TERM_MENU_ITEMS,
	LOAN_TYPE_MENU_ITEMS,
	ESCROW_MENU_ITEMS,
	LIEN_TYPE_MENU_ITEMS,
	PROPERTY_TYPE_MENU_ITEMS,
	PROPERTY_OCCUPANCY_MENU_ITEMS,
} from '../utils/constants'

export type LoanCalculatorSharedFieldsProps = {
	loanLocked?: boolean
}

const LoanCalculatorSharedFields = ({
	loanLocked = false,
}: LoanCalculatorSharedFieldsProps) => {
	const { isLoanAdmin } = useUser()

	const { setValue, watch, trigger, getValues } = useFormContext()

	const loanPurpose = watch(
		'loanPurpose'
	) as keyof typeof LoanPurposeTypes

	const onPropertyValueOrDownPaymentAmountChange = async (
		e: FocusEvent<HTMLInputElement>
	) => {
		const { value, name } = e.target

		const isValid = await trigger(name)

		if (!isValid) {
			return
		}

		const downPaymentAmount =
			name === 'downPaymentAmount'
				? value
				: getValues('downPaymentAmount')
		const propertyValue =
			name === 'propertyValue' ? value : getValues('propertyValue')

		const price = currencyToFloat(propertyValue || '0.00')
		const payment = currencyToFloat(downPaymentAmount || '0.00')

		const loanAmount = price.minus(payment)
		const percent = payment.dividedBy(price).times(100).toFixed(3)

		setValue('downPaymentPercent', percent)
		setValue('loanAmount', applyMask(loanAmount, 'fullCurrency'))

		if (name === 'propertyValue') {
			setValue('propertyValue', applyMask(value, 'fullCurrency'))
		}

		if (name === 'downPaymentAmount') {
			setValue('downPaymentAmount', applyMask(value, 'fullCurrency'))
		}
	}

	const onDownPaymentPercentChange = (
		e: FocusEvent<HTMLInputElement>
	) => {
		const { value } = e.target
		trigger('downPaymentPercent').then((isValid) => {
			if (!isValid) {
				return
			}
			const propertyValue = getValues('propertyValue')

			const price = currencyToFloat(propertyValue || '0.00')

			const percent = Decimal(value || '0')

			const payment = percent.dividedBy(100).times(price)
			const loanAmount = price.minus(payment)

			setValue(
				'downPaymentAmount',
				applyMask(payment, 'fullCurrency')
			)
			setValue('loanAmount', applyMask(loanAmount, 'fullCurrency'))
		})
	}

	const onLoanAmountChange = (e: FocusEvent<HTMLInputElement>) => {
		const { value } = e.target
		trigger('loanAmount').then((isValid) => {
			if (!isValid) {
				return
			}
			const propertyValue = getValues('propertyValue')

			const loanAmount = currencyToFloat(value || '0.00')

			const price = currencyToFloat(propertyValue || '0.00')

			const payment = price.minus(loanAmount)
			const percent = payment.dividedBy(price).times(100)

			setValue(
				'downPaymentAmount',
				applyMask(payment, 'fullCurrency')
			)
			setValue('downPaymentPercent', percent)
			setValue('loanAmount', applyMask(value, 'fullCurrency'))
		})
	}

	const handleZipChange = async (e: FocusEvent<HTMLInputElement>) => {
		const { value } = e.target
		trigger('zipCode').then(async (isValid) => {
			if (!isValid) {
				return
			}

			try {
				const { city, state, county } =
					await getCityCountyStateFromZip(value)

				if (city) {
					setValue('city', city)
				}

				if (state) {
					setValue('state', state)
				}

				if (county) {
					setValue('county', county)
				}
			} catch {
				setValue('city', '')
				setValue('state', '')
				setValue('county', '')
			}
		})
	}

	return (
		<>
			<Select
				name="loanPurpose"
				label="Loan Purpose"
				disabled={loanLocked || !isLoanAdmin}
				menuItems={[
					{
						label: 'Loan Purpose',
						value: '',
					},
					{
						label: 'Purchase',
						value: LoanPurposeTypes.Purchase,
					},
					{
						label: 'Refi',
						value: LoanPurposeTypes.NoCashOutRefinance,
					},
					{
						label: 'Cash Out Refi',
						value: LoanPurposeTypes.CashOutRefinance,
					},
				]}
				fullWidth
			/>
			<Select
				name="loanType"
				label="Loan Type"
				disabled={loanLocked || !isLoanAdmin}
				menuItems={LOAN_TYPE_MENU_ITEMS}
				fullWidth
			/>
			<MoneyField
				name="propertyValue"
				label="Property Value"
				disabled={loanLocked}
				onBlur={onPropertyValueOrDownPaymentAmountChange}
				fullWidth
				isFullCurrency
			/>
			{loanPurpose === LoanPurposeTypes[LoanPurposeTypes.Purchase] &&
				isLoanAdmin && (
					<>
						<MoneyField
							name="downPaymentAmount"
							label="Down Payment Amount"
							disabled={loanLocked}
							onBlur={onPropertyValueOrDownPaymentAmountChange}
							fullWidth
							isFullCurrency
						/>
						<PercentField
							name="downPaymentPercent"
							label="Down Payment Percent"
							disabled={loanLocked}
							onBlur={onDownPaymentPercentChange}
							fullWidth
						/>
					</>
				)}
			<MoneyField
				name="loanAmount"
				label="Loan Amount"
				disabled={loanLocked}
				onBlur={onLoanAmountChange}
				fullWidth
				isFullCurrency
			/>
			<ZipCodeField
				name="zipCode"
				label="Zip Code"
				disabled={loanLocked}
				onBlur={handleZipChange}
				fullWidth
			/>
			{isLoanAdmin && (
				<TextField
					type="number"
					name="creditScore"
					label="Credit Score"
					disabled={loanLocked}
					inputProps={{ maxLength: 3, max: 850, min: 300 }}
					fullWidth
				/>
			)}
			<MoneyField
				name="taxes"
				label="Monthly Taxes"
				disabled={loanLocked}
				fullWidth
				isFullCurrency
			/>
			<MoneyField
				name="insurance"
				label="Monthly Insurance"
				disabled={loanLocked}
				fullWidth
				isFullCurrency
			/>
			<Select
				name="escrow"
				label="Escrow"
				disabled={loanLocked || !isLoanAdmin}
				menuItems={ESCROW_MENU_ITEMS}
				fullWidth
			/>
			<MoneyField
				name="totalAssets"
				label="Total Assets"
				disabled
				fullWidth
			/>
			<Select
				name="loanTerm1"
				label="Loan Term 1"
				disabled={loanLocked || !isLoanAdmin}
				menuItems={[
					{
						label: 'Loan Term 1',
						value: '',
					},
					...SHARED_LOAN_TERM_MENU_ITEMS,
				]}
				fullWidth
			/>
			<Select
				name="loanTerm2"
				label="Loan Term 2"
				disabled={loanLocked || !isLoanAdmin}
				menuItems={[
					{
						label: 'Loan Term 2',
						value: '',
					},
					...SHARED_LOAN_TERM_MENU_ITEMS,
				]}
				fullWidth
			/>
			<Select
				name="propertyOccupancy"
				label="Property Occupancy"
				disabled={loanLocked || !isLoanAdmin}
				menuItems={PROPERTY_OCCUPANCY_MENU_ITEMS}
				fullWidth
			/>
			<Select
				name="propertyType"
				label="Home Type"
				disabled={loanLocked || !isLoanAdmin}
				menuItems={PROPERTY_TYPE_MENU_ITEMS}
				fullWidth
			/>
			<MoneyField
				name="borrowerIncome"
				label="Borrower Income"
				disabled
				fullWidth
				isFullCurrency
			/>
			<Select
				name="lienType"
				label="Lien Type"
				disabled={loanLocked || !isLoanAdmin}
				menuItems={LIEN_TYPE_MENU_ITEMS}
				fullWidth
			/>
			<PercentField
				name="rate"
				label="Rate"
				disabled={loanLocked || !isLoanAdmin}
				fullWidth
			/>
			<TextField name="county" label="County" disabled={loanLocked} />
			<MoneyField
				name="flood"
				label="Monthly Flood"
				disabled={loanLocked}
				fullWidth
				isFullCurrency
			/>
			<MoneyField
				name="hoa"
				label="Monthly HOA"
				disabled={loanLocked}
				fullWidth
				isFullCurrency
			/>
			<PercentField
				name="miFactor"
				label="MI Factor"
				disabled
				fullWidth
			/>
			<MoneyField name="mi" label="MI" disabled fullWidth />
		</>
	)
}

export default LoanCalculatorSharedFields
